import {
  ATTACHMENT_MAX_BYTES,
  type Attachment,
  isAllowedContentType,
  type PresignedUpload,
} from '@servicetim/shared';
import { errorMessage } from '@servicetim/ui/lib';

/*
 * Attachment upload state machine (S-05-09): presign → direct POST to S3 (with progress) →
 * complete. Pure reducer + runner so the flow is unit-tested without a browser; the card wires
 * it to the API hooks and an XHR uploader.
 */

export type UploadStatus = 'presigning' | 'uploading' | 'completing' | 'done' | 'error';

export interface UploadFile {
  name: string;
  size: number;
  type: string;
}

export interface UploadItem {
  id: string;
  file: UploadFile;
  status: UploadStatus;
  /** 0..1 of the S3 POST. */
  progress: number;
  attachmentId: string | null;
  error: string | null;
}

export type UploadEvent =
  | { type: 'added'; id: string; file: UploadFile }
  | { type: 'presigned'; id: string; attachmentId: string }
  | { type: 'progress'; id: string; progress: number }
  | { type: 'uploaded'; id: string }
  | { type: 'completed'; id: string }
  | { type: 'failed'; id: string; error: string }
  | { type: 'retry'; id: string }
  | { type: 'dismissed'; id: string };

const clamp = (n: number) => Math.min(1, Math.max(0, n));

const patch = (
  state: readonly UploadItem[],
  id: string,
  change: Partial<UploadItem>,
): UploadItem[] => state.map((item) => (item.id === id ? { ...item, ...change } : item));

export function uploadReducer(state: readonly UploadItem[], event: UploadEvent): UploadItem[] {
  switch (event.type) {
    case 'added':
      return [
        ...state,
        {
          id: event.id,
          file: event.file,
          status: 'presigning',
          progress: 0,
          attachmentId: null,
          error: null,
        },
      ];
    case 'presigned':
      return patch(state, event.id, { attachmentId: event.attachmentId, status: 'uploading' });
    case 'progress':
      return patch(state, event.id, { status: 'uploading', progress: clamp(event.progress) });
    case 'uploaded':
      return patch(state, event.id, { status: 'completing', progress: 1 });
    case 'completed':
      return patch(state, event.id, { status: 'done', progress: 1, error: null });
    case 'failed':
      return patch(state, event.id, { status: 'error', error: event.error });
    case 'retry':
      return patch(state, event.id, {
        status: 'presigning',
        progress: 0,
        attachmentId: null,
        error: null,
      });
    case 'dismissed':
      return state.filter((item) => item.id !== event.id);
  }
}

export const isUploadActive = (item: UploadItem): boolean =>
  item.status === 'presigning' || item.status === 'uploading' || item.status === 'completing';

/** Client-side checks that would otherwise fail at presign (413 / 400). */
export function validateFile(file: UploadFile): string | null {
  if (file.size <= 0) return 'The file is empty.';
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `Files are limited to ${Math.round(ATTACHMENT_MAX_BYTES / 1024 / 1024)} MB.`;
  }
  const type = file.type || 'application/octet-stream';
  if (!isAllowedContentType(type)) return `Files of type ${type} are not allowed.`;
  return null;
}

export type UploadTarget = PresignedUpload['upload'];

export interface UploadDeps {
  /** `POST /tickets/{id}/attachments` → pending row + presigned POST. */
  presign: (file: File) => Promise<PresignedUpload>;
  /** The S3 multipart POST; reports progress in 0..1. */
  upload: (
    target: UploadTarget,
    file: File,
    onProgress: (fraction: number) => void,
  ) => Promise<void>;
  /** `POST /attachments/{aid}/complete`. */
  complete: (attachmentId: string) => Promise<Attachment>;
}

/**
 * Drives one file through the three steps, dispatching every state change. Resolves the ready
 * attachment, or `null` after a `failed` event (the error text is in the state).
 */
export async function runUpload(
  id: string,
  file: File,
  deps: UploadDeps,
  dispatch: (event: UploadEvent) => void,
): Promise<Attachment | null> {
  try {
    const presigned = await deps.presign(file);
    dispatch({ type: 'presigned', id, attachmentId: presigned.attachmentId });
    await deps.upload(presigned.upload, file, (fraction) =>
      dispatch({ type: 'progress', id, progress: fraction }),
    );
    dispatch({ type: 'uploaded', id });
    const attachment = await deps.complete(presigned.attachmentId);
    dispatch({ type: 'completed', id });
    return attachment;
  } catch (error) {
    dispatch({ type: 'failed', id, error: errorMessage(error) });
    return null;
  }
}

/** Browser uploader: `XMLHttpRequest` so the S3 POST reports upload progress. */
export function xhrUpload(
  target: UploadTarget,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [key, value] of Object.entries(target.fields)) form.append(key, value);
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', target.url);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => reject(new Error('Upload to storage failed (network error)'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload to storage failed (${xhr.status})`));
    };
    xhr.send(form);
  });
}
